import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import type { ApparelStudy, StudyLayer } from "@erc/shared";
import { customApi } from "../../lib/customListing";
import { getBackendUrl } from "../../lib/http";

// Yazi disindaki alici degerleri: murekkep secimi (buyer_pick) ve foto yuvasi.
// Sablon onizlemesi ve ornek satirlari ayni girdileri kullanir.

/** Murekkep katmani (sabit cizim, rengini alici secer) ve foto yuvasi */
export const inkLayersOf = (layers: StudyLayer[]) => layers.filter((l) => l.colorRule === "buyer_pick" && (l.options?.length ?? 0) > 0);
export const photoLayersOf = (layers: StudyLayer[]) => layers.filter((l) => l.kind === "photo_slot");
/** Yazi katmaninin alici renk yuvalari (ana/ikinci…) — sunucudaki colorSlotsOf ile ayni kural */
export function colorSlotsOf(s: ApparelStudy, l: StudyLayer): Array<{ key: string; label: string; layer: StudyLayer }> {
  if (l.colorRule !== "buyer_pick" || (l.kind !== "font_text" && l.kind !== "glyph_text")) return [];
  // Secenekler sorudan: yazi katmaninin kendi options'i yok (Red/Green/White sorunun listesi)
  return s.questions.filter((q) => q.type === "dropdown" && q.layerKeys.includes(l.key))
    .map((q, i) => ({ key: `${l.key}__c${i + 1}`, label: q.question, layer: { ...l, options: q.options?.length ? q.options : l.options ?? Object.keys(l.optionHex ?? {}) } }));
}

const abs = (u: string) => (/^(https?:|data:)/.test(u) ? u : `${getBackendUrl()}${u.startsWith("/") ? "" : "/"}${u}`);

export function InkSelect({ layer, value, onChange, compact }: { layer: StudyLayer; value: string; onChange: (v: string) => void; compact?: boolean }) {
  const hex = layer.optionHex?.[value];
  return (
    <div className="flex items-center gap-2">
      <span className="h-4 w-4 flex-shrink-0 rounded-full border border-gray-300" style={{ background: hex ?? "transparent" }} aria-hidden />
      <select aria-label={layer.labelTr} value={value} onChange={(e) => onChange(e.target.value)}
        className={`${compact ? "w-36 py-1" : "w-full py-2"} rounded-lg border border-gray-200 px-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200`}>
        <option value="">Renk seç…</option>
        {(layer.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

/** Ornek fotograf: bos = sablonun ornek fotografi. Yuklenen gorsel foto yuvasina sigdirilir. */
export function PhotoInput({ recId, layer, value, onChange }: { recId: string; layer: StudyLayer; value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pick = async (f: File) => {
    setBusy(true); setErr(null);
    try {
      const dataUrl = await new Promise<string>((ok, bad) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => bad(r.error); r.readAsDataURL(f); });
      const r = await customApi<{ url: string }>("POST", `/${recId}/photo`, { dataUrl });
      onChange(r.url);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      {value
        ? <img src={abs(value)} alt={layer.labelTr} className="h-8 w-8 rounded border border-gray-200 object-cover" />
        : <span className="text-xs text-gray-500">Örnek fotoğraf</span>}
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} title="Fotoğraf yükle (şeffaf zeminli kesik en iyi sonucu verir)"
        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} {value ? "Değiştir" : "Yükle"}
      </button>
      {value && <button type="button" onClick={() => onChange("")} aria-label="Örnek fotoğrafa dön" title="Örnek fotoğrafa dön" className="rounded p-1 text-gray-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); e.target.value = ""; }} />
      {err && <span className="text-xs text-red-600">{err}</span>}
    </div>
  );
}
